import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../hooks';
import { uploadAvatar, removeAvatar } from '../../lib/api';
import { validateOptionalLinkedInUrl } from '../../lib/utils/setupLinkedInUrl';
import { hasActiveSubscription, needsSubscriptionSelection } from '../../lib/utils/subscriptionAccess';
import Switch from '../../components/ui/Switch';
import {
  SUPPORTED_LOCALES,
  applyLocale,
  getStoredLocale,
  normalizeLocale,
} from '../../i18n';

const Profile = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading, error, updateProfile, refetch } = useProfile();
  const [languageSaving, setLanguageSaving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyName, setCompanyName] = useState(profile?.company_name || '');
  const [companySaveLoading, setCompanySaveLoading] = useState(false);
  const [companySaveError, setCompanySaveError] = useState('');
  const [editingLinkedin, setEditingLinkedin] = useState(false);
  const [linkedinUrl, setLinkedinUrl] = useState(profile?.linkedin_url || '');
  const [linkedinSaveLoading, setLinkedinSaveLoading] = useState(false);
  const [linkedinSaveError, setLinkedinSaveError] = useState('');
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [reportsSaving, setReportsSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setFullName(profile?.full_name || '');
  }, [profile?.full_name]);

  useEffect(() => {
    setCompanyName(profile?.company_name || '');
  }, [profile?.company_name]);

  useEffect(() => {
    setLinkedinUrl(profile?.linkedin_url || '');
  }, [profile?.linkedin_url]);

  const noPlanSelected = needsSubscriptionSelection(profile);
  const planLabel = hasActiveSubscription(profile) && profile?.subscription_plan
    ? profile.subscription_plan.charAt(0).toUpperCase() + profile.subscription_plan.slice(1)
    : t('common.noPlanSelected');
  const currentLocale = normalizeLocale(profile?.preferred_locale || i18n.language || getStoredLocale());

  const languageOptions = SUPPORTED_LOCALES.map((code) => ({
    value: code,
    label: t(`languages.${code}`),
  }));

  const handleLanguageChange = async (locale) => {
    const next = normalizeLocale(locale);
    if (next === currentLocale || languageSaving) return;
    setLanguageSaving(true);
    try {
      await applyLocale(next);
      const { error: err } = await updateProfile({ preferred_locale: next });
      if (err) console.error('Failed to save language preference:', err);
    } finally {
      setLanguageSaving(false);
    }
  };

  const receiveWeeklyReports = profile?.receive_weekly_reports !== false;

  const handleWeeklyReportsChange = async (checked) => {
    if (reportsSaving) return;
    setReportsSaving(true);
    try {
      const { error: err } = await updateProfile({ receive_weekly_reports: checked });
      if (err) console.error('Failed to save weekly report preference:', err);
    } finally {
      setReportsSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const handleSaveName = async (e) => {
    e?.preventDefault();
    if (!fullName?.trim()) return;
    setSaveLoading(true);
    setSaveError('');
    const { error: err } = await updateProfile({ full_name: fullName.trim() });
    setSaveLoading(false);
    if (err) {
      setSaveError(err);
      return;
    }
    setEditingName(false);
  };

  const handleSaveCompany = async (e) => {
    e?.preventDefault();
    setCompanySaveLoading(true);
    setCompanySaveError('');
    const { error: err } = await updateProfile({ company_name: companyName.trim() || null });
    setCompanySaveLoading(false);
    if (err) {
      setCompanySaveError(err);
      return;
    }
    setEditingCompany(false);
  };

  const handleSaveLinkedin = async (e) => {
    e?.preventDefault();
    const validation = validateOptionalLinkedInUrl(linkedinUrl);
    if (validation.error) {
      setLinkedinSaveError(validation.error);
      return;
    }

    setLinkedinSaveLoading(true);
    setLinkedinSaveError('');
    const { error: err } = await updateProfile({ linkedin_url: validation.value });
    setLinkedinSaveLoading(false);
    if (err) {
      setLinkedinSaveError(err);
      return;
    }
    setLinkedinUrl(validation.value || '');
    setEditingLinkedin(false);
  };

  const handleAvatarChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !user?.id) return;
    setAvatarError('');
    setAvatarLoading(true);
    try {
      const url = await uploadAvatar(user.id, file);
      await updateProfile({ avatar_url: url });
      await refetch();
    } catch (err) {
      setAvatarError(err?.message || 'Upload failed');
    } finally {
      setAvatarLoading(false);
      e.target.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!profile?.avatar_url) return;
    setAvatarError('');
    setAvatarLoading(true);
    try {
      await removeAvatar(profile.avatar_url);
      await updateProfile({ avatar_url: null });
      await refetch();
    } catch (err) {
      setAvatarError(err?.message || 'Failed to remove photo');
    } finally {
      setAvatarLoading(false);
    }
  };

  if (loading && !profile) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">{t('profile.loadingProfile')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-md">
            <p className="text-destructive mb-4">{error}</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
              {t('profile.title')}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              {t('profile.subtitle')}
            </p>
          </div>

          <div className="space-y-6">
            {/* Profile info card */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-elevation-sm">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="User" size={20} />
                {t('profile.account')}
              </h2>
              <div className="space-y-4">
                {/* Avatar */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={handleAvatarChange}
                      disabled={avatarLoading}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={avatarLoading}
                      className="block rounded-full overflow-hidden w-[100px] h-[100px] border-2 border-border hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
                    >
                      {profile?.avatar_url ? (
                        <Image
                          src={profile.avatar_url}
                          alt={t('profile.avatarAlt')}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-semibold">
                          {getInitials(profile?.full_name)}
                        </div>
                      )}
                    </button>
                    {avatarLoading && (
                      <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{t('profile.profilePhoto')}</p>
                    <p className="text-xs text-muted-foreground mb-1">{t('profile.photoHint')}</p>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={avatarLoading}
                      >
                        {avatarLoading ? t('profile.uploading') : t('profile.changePhoto')}
                      </Button>
                      {profile?.avatar_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveAvatar}
                          disabled={avatarLoading}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          {t('profile.removePhoto')}
                        </Button>
                      )}
                    </div>
                    {avatarError && <p className="text-xs text-destructive mt-1">{avatarError}</p>}
                  </div>
                </div>
                {editingName ? (
                  <form onSubmit={handleSaveName} className="space-y-3">
                    <Input
                      label={t('profile.fullName')}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder={t('profile.yourName')}
                      error={saveError}
                      disabled={saveLoading}
                    />
                    <div className="flex gap-2">
                      <Button type="submit" loading={saveLoading} size="sm">
                        {t('common.save')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingName(false);
                          setFullName(profile?.full_name || '');
                          setSaveError('');
                        }}
                        disabled={saveLoading}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">{t('profile.fullName')}</p>
                      <p className="text-foreground font-medium">{profile?.full_name || '—'}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setEditingName(true)}>
                      {t('common.edit')}
                    </Button>
                  </div>
                )}
                {editingCompany ? (
                  <form onSubmit={handleSaveCompany} className="space-y-3">
                    <Input
                      label={t('profile.company')}
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder={t('profile.companyName')}
                      error={companySaveError}
                      disabled={companySaveLoading}
                    />
                    <div className="flex gap-2">
                      <Button type="submit" loading={companySaveLoading} size="sm">
                        {t('common.save')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingCompany(false);
                          setCompanyName(profile?.company_name || '');
                          setCompanySaveError('');
                        }}
                        disabled={companySaveLoading}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">{t('profile.company')}</p>
                      <p className="text-foreground font-medium">{profile?.company_name || '—'}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setEditingCompany(true)}>
                      {t('common.edit')}
                    </Button>
                  </div>
                )}
                {editingLinkedin ? (
                  <form onSubmit={handleSaveLinkedin} className="space-y-3">
                    <Input
                      label={t('profile.linkedinLink')}
                      type="url"
                      value={linkedinUrl}
                      onChange={(e) => setLinkedinUrl(e.target.value)}
                      placeholder="https://linkedin.com/company/your-company"
                      description={t('profile.linkedinHint')}
                      error={linkedinSaveError}
                      disabled={linkedinSaveLoading}
                    />
                    <div className="flex gap-2">
                      <Button type="submit" loading={linkedinSaveLoading} size="sm">
                        {t('common.save')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingLinkedin(false);
                          setLinkedinUrl(profile?.linkedin_url || '');
                          setLinkedinSaveError('');
                        }}
                        disabled={linkedinSaveLoading}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">{t('profile.linkedinLink')}</p>
                      {profile?.linkedin_url ? (
                        <a
                          href={profile.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline font-medium break-all"
                        >
                          {profile.linkedin_url}
                        </a>
                      ) : (
                        <p className="text-foreground font-medium">—</p>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setEditingLinkedin(true)}>
                      {t('common.edit')}
                    </Button>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground">{t('profile.email')}</p>
                  <p className="text-foreground">{profile?.email || '—'}</p>
                </div>
              </div>
            </div>

            {/* Language card */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-elevation-sm">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Languages" size={20} />
                {t('profile.language')}
              </h2>
              <Select
                label={t('languages.label')}
                description={t('languages.description')}
                options={languageOptions}
                value={currentLocale}
                onChange={handleLanguageChange}
                disabled={languageSaving}
              />
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-elevation-sm">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Mail" size={20} />
                {t('profile.weeklyReports')}
              </h2>
              <Switch
                label={t('profile.weeklyReportsLabel')}
                description={t('profile.weeklyReportsHint')}
                checked={receiveWeeklyReports}
                disabled={reportsSaving}
                onCheckedChange={handleWeeklyReportsChange}
              />
            </div>

            {/* Subscription card */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-elevation-sm">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="CreditCard" size={20} />
                {t('profile.subscription')}
              </h2>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('profile.plan')}</span>
                  <span className="font-medium text-foreground">{planLabel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('profile.status')}</span>
                  <span className="font-medium text-foreground">
                    {hasActiveSubscription(profile)
                      ? (profile?.subscription_status === 'over_limit'
                        ? t('common.overLimit')
                        : profile?.subscription_cancel_at_period_end
                          ? t('common.canceling')
                          : t('common.active'))
                      : profile?.subscription_status === 'past_due'
                        ? t('common.pastDue')
                        : profile?.subscription_status === 'expired'
                          ? t('common.expired')
                          : noPlanSelected
                            ? t('common.selectAPlan')
                            : (profile?.subscription_status || t('common.emDash'))}
                  </span>
                </div>
                {hasActiveSubscription(profile) && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('profile.candidateLimit')}</span>
                    <span className="font-medium text-foreground">{profile?.max_candidates ?? t('common.emDash')}</span>
                  </div>
                )}
              </div>
              <Button
                variant="default"
                size="sm"
                className="mt-4"
                onClick={() => navigate('/billing')}
              >
                {t('common.manageSubscription')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Profile;
